import * as THREE from "three/webgpu";
import { celestialTheme } from "../../design/celestialTheme";
import type { NexusEvent } from "../../domain/mesh";
import type { PresentedSnapshot } from "../../presentation/mesh";
import type { FocusState } from "../../store/nexusStore";
import { nodeVisibility, type SemanticZoomState } from "../../experience/semanticZoom";
import { focusTargetEntityId, focusedNodeId } from "../../store/nexusStore";
import { WORLD_ORIGIN } from "../config/worldFrame";
import { ResourceRegistry } from "../core/ResourceRegistry";
import { AttentionField } from "../attention/AttentionField";
import type { CameraFrame } from "../camera/CameraFramingPolicy";
import { resolveWorldFocusFrame } from "../camera/WorldFocusFraming";
import { CartographyLayoutEngine } from "../layout/CartographyLayoutEngine";
import { relationPhase, relationPhysics } from "../relations/relationPhysics";
import { RelationRegistry } from "../relations/RelationRegistry";
import { SingularityInteractionProjector } from "../interaction/SingularityInteractionProjector";
import { SpatialEntityRegistry } from "../spatial/SpatialEntityRegistry";
import { SpatialProjectionPipeline } from "../spatial/SpatialProjectionPipeline";
import { OrbitalTrackProjector } from "./OrbitalTrackProjector";
import { RelationPathProjector } from "./RelationPathProjector";
import { RelationPulseProjector } from "./RelationPulseProjector";
import {
  buildWorldNodes,
  buildWorldOrbitals,
  createFallbackWorldRelation,
  makeWorldMaterial,
  updateWorldSemantic,
  type EdgeRuntime,
  type NodeRuntime,
  type OrbitalRuntime,
  type SemanticUpdateContext,
  type LiveRelationPath,
} from "./WorldRuntime";
export class WorldScene {
  readonly visualAnchors = new Map<string, THREE.Vector3>();
  readonly interactive: THREE.Object3D[] = [];
  private resources = new ResourceRegistry();
  private readonly root = new THREE.Group();
  private readonly positions = new SpatialEntityRegistry();
  private readonly spatial = new SpatialProjectionPipeline();
  private readonly attention = new AttentionField();
  private readonly layout = new CartographyLayoutEngine();
  private readonly relationProjector = new RelationPathProjector(this.spatial);
  private readonly orbitalTrackProjector = new OrbitalTrackProjector(this.spatial);
  private readonly singularityInteraction = new SingularityInteractionProjector();
  private readonly nodeRuntimes = new Map<string, NodeRuntime>();
  private readonly edgeRuntimes: EdgeRuntime[] = [];
  private readonly relationPaths: LiveRelationPath[] = [];
  private readonly orbitalRuntimes = new Map<string, OrbitalRuntime>();
  private readonly relations = new RelationRegistry();
  private readonly pulse: THREE.Mesh;
  private readonly pulseGeometry: THREE.SphereGeometry;
  private readonly pulseMaterial: THREE.MeshBasicNodeMaterial;
  private readonly pulseProjector: RelationPulseProjector;
  private readonly scaleTarget = new THREE.Vector3();
  private focus: FocusState = { kind: "cosmos" };
  private hoveredEntityId: string | null = null;
  private singularityNodeId: string | null = null;
  private reducedMotion = false;
  private semanticZoom: SemanticZoomState = { level: 0, blend: 0, distance: 44 };
  private accessibleMode = false;
  private readonly semanticUpdateContext: SemanticUpdateContext = {
    attention: this.attention,
    focus: this.focus,
    hoveredEntityId: this.hoveredEntityId,
    reducedMotion: this.reducedMotion,
    accessibleMode: this.accessibleMode,
    relatedIds: this.relations.relatedIds(null),
    nodeRuntimes: this.nodeRuntimes,
    edgeRuntimes: this.edgeRuntimes,
    relationPaths: this.relationPaths,
    orbitalRuntimes: this.orbitalRuntimes,
    positions: this.positions,
    spatial: this.spatial,
    relationProjector: this.relationProjector,
    scaleTarget: this.scaleTarget,
  };
  constructor(private readonly scene: THREE.Scene) {
    this.scene.add(this.root);
    this.pulseGeometry = new THREE.SphereGeometry(0.07, 16, 16);
    this.pulseMaterial = makeWorldMaterial(celestialTheme.pulse, 2.8, 1);
    this.pulse = new THREE.Mesh(this.pulseGeometry, this.pulseMaterial);
    this.pulse.visible = false;
    this.pulse.frustumCulled = false;
    this.root.add(this.pulse);
    this.pulseProjector = new RelationPulseProjector(this.pulse, this.relationProjector, this.spatial);
  }

  rebuild(snapshot: PresentedSnapshot) {
    this.clearWorldObjects();
    this.resources.dispose();
    this.resources = new ResourceRegistry();
    const layout = this.layout.resolve(snapshot.nodes, snapshot.edges);
    this.singularityNodeId = buildWorldNodes(snapshot.nodes, layout, {
      resources: this.resources,
      positions: this.positions,
      spatial: this.spatial,
      visualAnchors: this.visualAnchors,
      nodeRuntimes: this.nodeRuntimes,
      interactive: this.interactive,
      root: this.root,
    });
    this.buildEdges(snapshot);
    this.buildOrbitals(snapshot);
    this.relations.rebuild(snapshot);
    this.applyFocusState();
  }
  private buildEdges(snapshot: PresentedSnapshot) {
    for (const edge of snapshot.edges) {
      const source = this.positions.get(edge.source);
      const target = this.positions.get(edge.target);
      const route = snapshot.projection.edges[edge.id];
      if (!source || !target || !route) continue;
      const physics = relationPhysics[edge.relation];
      const semanticRoute = {
        ...route,
        bend: route.bend * physics.bendScale,
        skew: route.skew * physics.skewScale,
        lift: route.lift * physics.liftScale,
      };
      const path = this.relationProjector.createPath(
        source.authoring,
        target.authoring,
        source.semantic,
        target.semantic,
        semanticRoute,
      );
      this.relations.register(edge.source, edge.target, path);
      this.relationPaths.push({ path, source: edge.source, target: edge.target, route: semanticRoute });
      const sourceNode = this.nodeRuntimes.get(edge.source)?.node;
      const targetNode = this.nodeRuntimes.get(edge.target)?.node;
      if (sourceNode?.archetype === "black-hole" || targetNode?.archetype === "black-hole") continue;

      const baseOpacity = (0.065 + edge.strength * 0.055) * route.opacityScale * physics.opacityScale;
      const runtime = this.relationProjector.createEdge(path, physics.hue, baseOpacity);
      this.resources.geometry(runtime.geometry);
      this.resources.material(runtime.material);
      this.root.add(runtime.line);
      this.edgeRuntimes.push({
        ...runtime,
        id: edge.id,
        source: edge.source,
        target: edge.target,
        physics,
        phase: relationPhase(edge.id),
      });
    }
  }

  private buildOrbitals(snapshot: PresentedSnapshot) {
    buildWorldOrbitals(snapshot, {
      resources: this.resources,
      positions: this.positions,
      orbitalTrackProjector: this.orbitalTrackProjector,
      visualAnchors: this.visualAnchors,
      orbitalRuntimes: this.orbitalRuntimes,
      interactive: this.interactive,
      root: this.root,
    });
  }

  setReducedMotion(reducedMotion: boolean) {
    this.reducedMotion = reducedMotion;
  }
  setAccessibleMode(enabled: boolean) {
    this.accessibleMode = enabled;
    this.spatial.setAccessibleMode(enabled);
    this.applySemanticVisibility();
  }
  setHovered(entityId: string | null) {
    this.hoveredEntityId = entityId;
  }
  setFocus(focus: FocusState) {
    this.focus = focus;
    this.applyFocusState();
    this.applySemanticVisibility();
  }
  setSemanticZoom(state: SemanticZoomState) {
    const changed = this.semanticZoom.level !== state.level || Math.abs(this.semanticZoom.blend - state.blend) > 0.02;
    this.semanticZoom.level = state.level;
    this.semanticZoom.blend = state.blend;
    this.semanticZoom.distance = state.distance;
    if (changed) this.applySemanticVisibility();
  }
  setPulse(event: NexusEvent | null) {
    this.attention.signal(event);
    if (!event?.targetNodeId) {
      this.pulseProjector.clear();
      return;
    }
    let binding = this.relations.binding(event.sourceNodeId, event.targetNodeId);
    if (!binding) {
      const fallback = createFallbackWorldRelation(
        event.sourceNodeId,
        event.targetNodeId,
        this.positions,
        this.relationProjector,
        this.relations,
      );
      if (fallback) {
        binding = fallback.binding;
        this.relationPaths.push(fallback.livePath);
      }
    }
    this.pulseProjector.set(event, binding?.path ?? null, binding?.reversed ?? false);
  }
  updateSemantic(delta: number, elapsed: number) {
    const focusNode = focusedNodeId(this.focus);
    this.semanticUpdateContext.focus = this.focus;
    this.semanticUpdateContext.hoveredEntityId = this.hoveredEntityId;
    this.semanticUpdateContext.reducedMotion = this.reducedMotion;
    this.semanticUpdateContext.accessibleMode = this.accessibleMode;
    this.semanticUpdateContext.relatedIds = this.getRelatedIds(focusNode);
    updateWorldSemantic(this.semanticUpdateContext, delta, elapsed);
  }

  updateVisual(delta: number, camera: THREE.PerspectiveCamera, viewportWidth: number, viewportHeight: number) {
    const singularity = this.getSingularitySemanticPosition();
    this.spatial.beginVisualFrame(camera, viewportWidth, viewportHeight, singularity);
    for (const [id, runtime] of this.nodeRuntimes) {
      const position = this.positions.get(id);
      if (!position || !runtime.root.visible) continue;
      if (runtime.node.archetype === "black-hole") position.visual.copy(position.semantic);
      else
        this.spatial.toVisual(
          position.semantic,
          singularity,
          camera,
          viewportWidth,
          viewportHeight,
          position.visual,
          "point",
        );
      runtime.root.position.copy(position.visual);
    }
    for (const [id, runtime] of this.orbitalRuntimes) {
      if (!runtime.satellite.visible) continue;
      const position = this.positions.get(id);
      const parent = this.positions.get(runtime.orbital.parentId);
      if (!position || !parent) continue;
      this.spatial.toVisual(
        position.semantic,
        singularity,
        camera,
        viewportWidth,
        viewportHeight,
        position.visual,
        "orbital",
      );
      runtime.satellite.position.copy(position.visual);
      runtime.interaction.position.copy(position.visual);
      if (runtime.track.line.visible)
        this.orbitalTrackProjector.update(
          runtime.track,
          parent.authoring,
          singularity,
          camera,
          viewportWidth,
          viewportHeight,
        );
    }
    for (const edge of this.edgeRuntimes)
      this.relationProjector.updateVisual(edge, singularity, camera, viewportWidth, viewportHeight);
    this.pulseProjector.update(delta, singularity, camera, viewportWidth, viewportHeight);
    this.spatial.endVisualFrame();
  }
  getRelatedIds(nodeId: string | null): ReadonlySet<string> {
    return this.relations.relatedIds(nodeId);
  }

  getSemanticNodePosition(nodeId: string | null) {
    return nodeId && this.nodeRuntimes.has(nodeId) ? this.positions.getSemantic(nodeId) : null;
  }

  getVisualNodeAnchor(nodeId: string | null) {
    return nodeId && this.nodeRuntimes.has(nodeId) ? this.positions.getVisual(nodeId) : null;
  }

  getSemanticOrbitalPosition(orbitalId: string | null) {
    return orbitalId && this.orbitalRuntimes.has(orbitalId) ? this.positions.getSemantic(orbitalId) : null;
  }

  getVisualOrbitalAnchor(orbitalId: string | null) {
    return orbitalId && this.orbitalRuntimes.has(orbitalId) ? this.positions.getVisual(orbitalId) : null;
  }

  getSemanticFocusPosition(focus: FocusState) {
    return this.positions.getSemantic(focusTargetEntityId(focus));
  }

  getFocusFrame(focus: FocusState): CameraFrame {
    if (focus.kind !== "node") return resolveWorldFocusFrame(focus, null, null, []);
    const node = this.nodeRuntimes.get(focus.nodeId)?.node ?? null;
    const origin = this.positions.getSemantic(focus.nodeId);
    const relatedPositions: THREE.Vector3[] = [];
    for (const relatedId of this.getRelatedIds(focus.nodeId)) {
      const position = this.positions.getSemantic(relatedId);
      if (position) relatedPositions.push(position);
    }
    return resolveWorldFocusFrame(focus, node, origin, relatedPositions);
  }

  getFocusArchetype(focus: FocusState): "stellar" | "black-hole" {
    if (focus.kind === "orbital") return "stellar";
    const entityId = focusTargetEntityId(focus);
    return entityId ? (this.nodeRuntimes.get(entityId)?.node.archetype ?? "stellar") : "stellar";
  }

  getSingularitySemanticPosition() {
    return this.positions.getSemantic(this.singularityNodeId) ?? WORLD_ORIGIN;
  }

  pickSingularity(
    camera: THREE.PerspectiveCamera,
    viewportWidth: number,
    viewportHeight: number,
    screenX: number,
    screenY: number,
  ) {
    if (!this.singularityNodeId) return null;
    return this.singularityInteraction.contains(
      camera,
      viewportWidth,
      viewportHeight,
      screenX,
      screenY,
      this.getSingularitySemanticPosition(),
    )
      ? this.singularityNodeId
      : null;
  }

  private applySemanticVisibility() {
    const focusNode = focusedNodeId(this.focus);
    const related = this.getRelatedIds(focusNode);
    for (const runtime of this.nodeRuntimes.values()) {
      if (runtime.node.archetype === "black-hole") continue;
      const rawFactor = nodeVisibility(runtime.node, this.semanticZoom, focusNode, related);
      const factor = this.accessibleMode
        ? Math.max(rawFactor, runtime.node.kind === "system" ? 0.72 : related.has(runtime.node.id) ? 0.46 : 0.12)
        : rawFactor;
      runtime.root.visible = factor > 0.04;
      const dim = focusNode !== null && !related.has(runtime.node.id);
      runtime.coreMaterial.opacity = factor * (dim ? 0.42 : 1);
      runtime.glowVisibility = factor * (dim ? 0.18 : 1);
      runtime.interaction.userData.pickEnabled = factor > 0.18;
    }
    for (const edge of this.edgeRuntimes) {
      const active = focusNode !== null && (edge.source === focusNode || edge.target === focusNode);
      edge.line.visible = this.semanticZoom.level >= 1 || active;
    }
  }

  private applyFocusState() {
    const focusNode = focusedNodeId(this.focus);
    for (const edge of this.edgeRuntimes) {
      const active = focusNode !== null && (edge.source === focusNode || edge.target === focusNode);
      edge.material.opacity = active
        ? Math.min(edge.baseOpacity + 0.075, 0.19)
        : focusNode !== null
          ? 0.018
          : edge.baseOpacity;
    }
    this.applySemanticVisibility();
  }

  private clearWorldObjects() {
    for (const runtime of this.nodeRuntimes.values()) this.root.remove(runtime.root);
    for (const edge of this.edgeRuntimes) this.root.remove(edge.line);
    for (const runtime of this.orbitalRuntimes.values())
      this.root.remove(runtime.track.line, runtime.satellite, runtime.interaction);
    this.nodeRuntimes.clear();
    this.edgeRuntimes.length = 0;
    this.relationPaths.length = 0;
    this.orbitalRuntimes.clear();
    this.positions.clear();
    this.visualAnchors.clear();
    this.relations.clear();
    this.interactive.length = 0;
    this.singularityNodeId = null;
    this.attention.clear();
    this.pulseProjector.clear();
    this.root.add(this.pulse);
  }

  dispose() {
    this.scene.remove(this.root);
    this.resources.dispose();
    this.pulseGeometry.dispose();
    this.pulseMaterial.dispose();
  }
}
